import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DashboardChart, { ChartData } from './DashboardChart';
import DynamicFilterPanel, { DynamicFilterConfig } from '../../../components/DynamicComponent/DynamicFilterPanel';
import useApi from '../../../services/useApi';
import dayjs from 'dayjs';
import { getMNOColor } from '../../../utils/chartColors';

// Types for API response
interface MNOBalanceDataset {
  label: string;
  data: number[];
}

interface MNOBalanceApiResponse {
  period: string[];
  datasets: MNOBalanceDataset[];
}

const MnoAvailableBalance: React.FC = () => {
  const { getAPI } = useApi();
  const hasInitialized = useRef(false);
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [chartData, setChartData] = useState<ChartData>({ labels: [], datasets: [] });
  const [balancesByMno, setBalancesByMno] = useState<Record<string, number>>({});
  const [totalBalance, setTotalBalance] = useState<number>(0);
  
  // Internal search params for filters (not URL-based)
  const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams());
  
  // Dynamic filter configuration
  const filterConfig: DynamicFilterConfig = {
    dateRangeDropdown: {
      label: "Duration",
      type: "dateRangeDropdown",
      required: false,
      searchKey: { 
        date: "date", 
        start: "start_date", 
        end: "end_date" 
      },
    },
  };

  // Function to fetch MNO available balance data - using same pattern as fetchETopUpStockTrend
  const fetchMNoBalance = useCallback(async (filters: { [key: string]: string | string[] | boolean | null } = {}) => {
    setLoading(true);
    setError(undefined);

    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add date range filters
      if (filters.start_date && filters.end_date) {
        params.append('start_date', filters.start_date as string);
        params.append('end_date', filters.end_date as string);
      }
      
      // Add date filter type
      if (filters.filter_type && filters.filter_type !== '') {
        params.append('date', filters.filter_type as string);
      }
      
      // Add client timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      params.append('client_timezone', timezone);
      
      const endpoint = `dashboard/mno-available-balance${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await getAPI(endpoint);
      const response1 = await getAPI('dashboard/mno-target-achievement?client_timezone=' + timezone);
      console.log('response1', response1);
      if (response.error) {
        setError('Failed to fetch MNO available balance data');
        return;
      }
      
      const apiData: MNOBalanceApiResponse = response.data;
      
      // Process the API data for chart
      if (!apiData.period || !apiData.datasets || !Array.isArray(apiData.datasets)) {
        setChartData({ labels: [], datasets: [] });
        return;
      }

      // Capture per-MNO available balances and compute total
      const apiDataWithBalances = response.data as MNOBalanceApiResponse & { [key: string]: unknown } & { ["mno-available-balance"]?: Record<string, number> };
      const balanceMap: Record<string, number> = apiDataWithBalances["mno-available-balance"] || {};
      setBalancesByMno(balanceMap);
      const total = Object.values(balanceMap).reduce((sum: number, v) => sum + Number(v || 0), 0);
      setTotalBalance(total);

      // Process datasets with colors
      const processedDatasets = apiData.datasets.map((dataset) => {
        const color = getMNOColor(dataset.label);
        return {
          ...dataset,
          backgroundColor: color, // Solid color instead of transparent
          borderColor: color,
          borderWidth: 0, // Remove border
          fill: false
        };
      });

      const processedData: ChartData = {
        labels: apiData.period,
        datasets: processedDatasets
      };

      console.log('MNO Balance Processed Chart Data:', processedData);
      setChartData(processedData);
      
    } catch (err) {
      console.error('Error fetching MNO available balance:', err);
      setError('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [getAPI]);

  // Handle filter application - using same logic as handleMNOFilterApply from index.tsx
  const handleFilterApply = useCallback((filters: { [key: string]: string | string[] | boolean | null }) => {
    console.log('MNO Available Balance Filters applied:', filters);
    
    // Update internal search params
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Clear existing filter parameters
    newSearchParams.delete('date');
    newSearchParams.delete('start_date');
    newSearchParams.delete('end_date');
    
    // Convert dateRangeDropdown filters to the expected format
    const processedFilters: { [key: string]: string | string[] | boolean | null } = {};
    
    if (filters.date) {
      // Add to internal search params
      newSearchParams.set('date', filters.date as string);
      
      // Map the date range selection to filter_type
      switch (filters.date) {
        case 'Today':
          processedFilters.filter_type = 'Today';
          break;
        case 'Yesterday':
          processedFilters.filter_type = 'Yesterday';
          break;
        case 'This Month':
          processedFilters.filter_type = 'This Month';
          break;
        case 'This Year':
          processedFilters.filter_type = 'This Year';
          break;
        case 'Custom':
          processedFilters.filter_type = 'Custom Date';
          if (filters.start_date) {
            processedFilters.start_date = dayjs(filters.start_date as string).format('YYYY-MM-DD');
            newSearchParams.set('start_date', filters.start_date as string);
          }
          if (filters.end_date) {
            processedFilters.end_date = dayjs(filters.end_date as string).format('YYYY-MM-DD');
            newSearchParams.set('end_date', filters.end_date as string);
          }
          break;
        default:
          processedFilters.filter_type = 'This Year';
      }
    } else {
      processedFilters.filter_type = 'This Year';
    }
    
    // Update internal search params
    setSearchParams(newSearchParams);
    fetchMNoBalance(processedFilters);
  }, [fetchMNoBalance, searchParams]);

  // Handle filter clear - using same pattern as index.tsx
  const handleFilterClear = useCallback(() => {
    console.log('MNO Available Balance filters cleared');
    // Clear internal search params
    setSearchParams(new URLSearchParams());
    fetchMNoBalance({});
  }, [fetchMNoBalance]);

  // Initial data fetch
  useEffect(() => {
    if (!hasInitialized.current) {                                        
      hasInitialized.current = true;
      fetchMNoBalance();
    }
  }, [fetchMNoBalance]);

  // Chart options for bar chart
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          boxWidth: 8,
          boxHeight: 8,
          font: {
            size: 12,
            family: 'Montserrat, sans-serif',
            weight: '600'
          }
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: function(context: { dataset: { label: string }; parsed: { y: number } }) {
            return `${context.dataset.label}: AFA ${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.02)',
          lineWidth: 1,
          drawBorder: false
        },
        ticks: {
          font: {
            size: 11,
            family: 'Montserrat, sans-serif',
            weight: 'bold'
          },
          color: '#666',
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        display: true,
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.02)',
          lineWidth: 1,
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12,
            family: 'Montserrat, sans-serif',
            weight: '600'
          },
          color: '#666',
          callback: function(value: number | string) {
            return `AFA ${Number(value)}`;
          }
        }
      }
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 0, // Changed from 4 to 0 for rectangular bars
        borderSkipped: false
      }
    }
  }), []);

  return (
    <div className="w-full px-4 mb-10">
      <p className="text-2xl font-[600]  pb-4 py-1 text-primary_text">MNO Available Balance</p>      
      <DashboardChart
        type="bar"
        data={chartData}
        options={chartOptions}
        height={760}
        loading={loading}
        error={error}
        topbar={{
          rightContent: (
            <DynamicFilterPanel
              config={filterConfig}
              onApply={handleFilterApply}
              onClear={handleFilterClear}
              loading={loading}
              searchParams={searchParams}
              className="ml-2"
            />
          )
        }}
        belowTopbar={(
          <div className="w-full flex items-start justify-between py-2">
            {/* Left: Total Available Balance */}
            <div className="flex flex-col">
              <h1 className="text-title1 font-semibold">Total Available Balance</h1>
              <div className="bg-p_primary text-white rounded-lg px-6 py-4">
                <div className="text-lg font-medium text-center">AFA</div>
                <div className="text-xl text-center font-semibold">{totalBalance.toFixed(2)}</div>
              </div>
            </div>

            {/* Right: Per-MNO balances */}
            <div className="flex items-center">
              <div className="hidden md:flex flex-col space-y-2">
                {Object.entries(balancesByMno).map(([label, amount]) => {
                  const color = getMNOColor(label);
                  return (
                    <div key={label} className="flex items-center text-sm text-secondary_text">
                      <span className="inline-block w-4 h-4 rounded mr-2" style={{ backgroundColor: color }}></span>
                      <span className="mr-2 text-black font-medium">{label}:</span>
                      <span className="text-primary_text font-semibold">AFA {Number(amount || 0).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        belowTopbarHeight={100}
        showLegend={true}
        legendNote="Note: Tap an MNO name to view or hide its chart."
        extraTopMargin={true}
      />
    </div>
  );
};

export default MnoAvailableBalance;
